/**
 * ConditionChips
 *
 * Horizontal row of condition / allergy chips for the patient header strip.
 * Allergy chips are visually distinct (red) so they catch the eye before any
 * prescribing decision is made.
 *
 * Pure presentational — caller provides the list of conditions and the allergy
 * text (or undefined). Renders nothing if both are empty.
 */

import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ConditionChipsProps {
  conditions?: string[];
  /** Free-text allergy summary. "none" / "nkda" suppress the chip. */
  allergies?: string | null;
  className?: string;
}

const ALLERGY_NEGATIVES = new Set(['none', 'nkda', 'no known allergies', 'n/a', 'na', '']);

const ConditionChips: React.FC<ConditionChipsProps> = ({
  conditions,
  allergies,
  className,
}) => {
  const cleanConditions = (conditions ?? [])
    .map((c) => c?.trim())
    .filter((c): c is string => Boolean(c));

  const allergyText = allergies?.trim() ?? '';
  const showAllergy = allergyText && !ALLERGY_NEGATIVES.has(allergyText.toLowerCase());

  if (cleanConditions.length === 0 && !showAllergy) return null;

  return (
    <div className={cn('flex flex-wrap items-center gap-1.5', className)}>
      {cleanConditions.map((c) => (
        <span
          key={c}
          className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs font-medium text-slate-700"
        >
          {c}
        </span>
      ))}
      {showAllergy && (
        <span className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-700">
          <AlertTriangle className="h-3 w-3" />
          Allergy: {allergyText}
        </span>
      )}
    </div>
  );
};

export default ConditionChips;
